import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useT } from '@/i18n/LocaleContext';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { LanguageSwitcher } from './LanguageSwitcher';
import { cn } from '@/lib/cn';

const NAV_ITEMS = [
  { path: '/plataforma', key: 'nav.plataforma' },
  { path: '/cases', key: 'nav.cases' },
  { path: '/ciencia', key: 'nav.ciencia' },
  { path: '/ecossistema', key: 'nav.ecossistema' },
  { path: '/futuro', key: 'nav.futuro' },
  { path: '/contato', key: 'nav.contato' },
];

export function SiteHeader() {
  const t = useT('common');
  const { localizedTo } = useLocalizedNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // fecha drawer ao trocar de rota
    setMobileOpen(false);
  }, [localizedTo]);

  return (
    <header
      className={cn(
        'sticky top-0 z-40 border-b transition-colors duration-300',
        scrolled
          ? 'border-vyxora-cyan-400/20 bg-vyxora-abyss/85 backdrop-blur-xl'
          : 'border-white/5 bg-vyxora-bg-dark/60 backdrop-blur-md',
      )}
    >
      <div className="container-site flex items-center justify-between py-4">
        {/* Logo */}
        <Link to={localizedTo('/')} className="flex items-center" aria-label="VYXORA">
          <img
            src="/imagens/logo_principal.png"
            alt="VYXORA"
            className="h-9 w-auto animate-glow-pulse"
          />
        </Link>

        {/* Nav desktop */}
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={localizedTo(item.path)}
              className={({ isActive }) =>
                cn(
                  'text-sm font-medium transition',
                  isActive
                    ? 'text-vyxora-cyan-300'
                    : 'text-vyxora-text-light/70 hover:text-vyxora-cyan-300',
                )
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        {/* Direita: switcher + CTA */}
        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher compact />
          <Link
            to={localizedTo('/login')}
            className="inline-flex items-center gap-2 rounded-md border border-vyxora-cyan-400/40 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-vyxora-cyan-300 transition hover:border-vyxora-cyan-400 hover:bg-vyxora-cyan-400/10 hover:shadow-glow"
          >
            {t('cta.acessarPainel')}
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="rounded-md p-2 text-vyxora-text-light lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Drawer mobile */}
      {mobileOpen && (
        <div className="border-t border-vyxora-cyan-400/10 bg-vyxora-abyss/95 backdrop-blur-xl lg:hidden">
          <nav className="container-site flex flex-col gap-1 py-4">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={localizedTo(item.path)}
                className={({ isActive }) =>
                  cn(
                    'rounded px-3 py-3 text-sm font-medium transition',
                    isActive
                      ? 'bg-vyxora-cyan-400/10 text-vyxora-cyan-300'
                      : 'text-vyxora-text-light/80 hover:bg-white/5',
                  )
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <div className="mt-3 flex flex-col gap-3 border-t border-white/5 pt-4">
              <LanguageSwitcher compact={false} />
              <Link
                to={localizedTo('/login')}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-vyxora-cyan-400/40 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-vyxora-cyan-300 transition hover:border-vyxora-cyan-400 hover:bg-vyxora-cyan-400/10"
              >
                {t('cta.acessarPainel')}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
